import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import styled, { keyframes } from "styled-components";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { DISPLAY_PRODUCT_IMAGE_DIMENSION } from "@faire/retailer/views/product-based-collection/__internal__/DisplayCollectionPromo/DisplayStickyActionBanner";

const AddToCartAnimationBase: React.FC<{
  onAnimationComplete: () => void;
  displayProductImageUrl?: string;
}> = ({ onAnimationComplete, displayProductImageUrl }) => {
  const isSSR = useIsSSR();
  React.useEffect(() => {
    const timeout = setTimeoutIfPossible(() => {
      onAnimationComplete();
    }, 3000);
    return () => clearTimeout(timeout);
  }, [onAnimationComplete]);
  return (
    <AnimationContainer align="center">
      <ProductImage
        src={optimizeImageUrl(
          {
            url: displayProductImageUrl,
          },
          isSSR,
          {
            width: DISPLAY_PRODUCT_IMAGE_DIMENSION,
            height: DISPLAY_PRODUCT_IMAGE_DIMENSION,
          }
        )}
      />
      <Spacer size={12} />
      <Typography variant="paragraphSansMedium">
        <StrictLocalMsg
          defaultMessage="Added to bag!"
          description={{
            text: "The product(s) have been added to your bag",
          }}
        />
      </Typography>
    </AnimationContainer>
  );
};

export const AddToCartAnimation = observer(AddToCartAnimationBase);

const fadeIn = keyframes`
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
`;

const AnimationContainer = styled(Row)`
  background-color: ${Core.surface.inverse};
  position: sticky;
  z-index: 5;
  bottom: ${getSpacing(12)};
  right: ${getSpacing(12)};
  max-height: ${DISPLAY_PRODUCT_IMAGE_DIMENSION + 24}px;
  width: 360px;
  max-width: calc(100% - ${getSpacing("3x")});
  padding: ${getSpacing(12)};
  border: 1px solid ${Core.border.muted};
  margin-left: auto;
  animation: ${fadeIn} 1s ease-in-out;
`;

const ProductImage = styled.img`
  max-height: ${DISPLAY_PRODUCT_IMAGE_DIMENSION}px;
  max-width: ${DISPLAY_PRODUCT_IMAGE_DIMENSION}px;
  width: ${DISPLAY_PRODUCT_IMAGE_DIMENSION}px;
  height: ${DISPLAY_PRODUCT_IMAGE_DIMENSION}px;
`;
